import { useEffect, useState } from 'react';

const CATALOG_TITLE = 52;
const MIN_CATALOG_HEIGHT = 100;
const NOVINKI_ESTIMATE = 280;

const readHeroHeight = () => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--hero-height').trim();
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 423;
};

const measureSidebarChrome = (sidebar) => {
  if (!sidebar || window.innerWidth <= 768) {
    return CATALOG_TITLE;
  }

  const gap = parseFloat(getComputedStyle(sidebar).gap || '0') || 14;
  let chrome = CATALOG_TITLE + gap;

  const novinki = sidebar.querySelector('[data-sidebar-part="novinki"]');
  const contacts = sidebar.querySelector('[data-sidebar-part="contacts"]');

  if (novinki) {
    chrome += novinki.getBoundingClientRect().height + gap;
  } else {
    chrome += NOVINKI_ESTIMATE + gap;
  }

  if (contacts) {
    chrome += contacts.getBoundingClientRect().height;
  }

  return chrome;
};

/**
 * Высота прокрутки каталога = фиксированный постер + блок справа (категории / товары),
 * без измерения heroTopGroup целиком (иначе сайдбар раздувает строку grid и ломает расчёт).
 */
export function useCatalogSyncHeight({
  enabled,
  isHomePage,
  isCategoryPage,
  isProductPage,
  pathname,
}) {
  const [catalogMaxHeight, setCatalogMaxHeight] = useState(() => (
    enabled ? Math.max(MIN_CATALOG_HEIGHT, readHeroHeight() - CATALOG_TITLE - NOVINKI_ESTIMATE) : null
  ));

  useEffect(() => {
    if (!enabled) {
      setCatalogMaxHeight(null);
      return undefined;
    }

    const measure = () => {
      const container = document.querySelector('.container');
      if (!container) {
        return;
      }

      const gap = parseFloat(getComputedStyle(container).gap || '0') || 0;
      const sidebar = container.querySelector(':scope > section');
      const posterHeight = readHeroHeight();

      let rightHeight = posterHeight;

      if (isHomePage) {
        const categoriesSection = container.querySelector('.heroCategoriesSlot > section');
        if (categoriesSection) {
          rightHeight += gap + categoriesSection.getBoundingClientRect().height;
        }
      } else if (isCategoryPage) {
        const categoryBlock = container.querySelector('.mainContent > section');
        if (categoryBlock) {
          rightHeight += gap + categoryBlock.getBoundingClientRect().height;
        }
      } else if (isProductPage) {
        const productBlock = container.querySelector('.mainContent > section');
        rightHeight = productBlock?.scrollHeight ?? posterHeight;
      }

      const chrome = measureSidebarChrome(sidebar);
      setCatalogMaxHeight(Math.max(MIN_CATALOG_HEIGHT, Math.round(rightHeight - chrome)));
    };

    measure();

    const container = document.querySelector('.container');
    const observer = new ResizeObserver(measure);

    if (container) {
      observer.observe(container);

      const categoriesSection = container.querySelector('.heroCategoriesSlot > section');
      const mainSection = container.querySelector('.mainContent > section');
      const sidebar = container.querySelector(':scope > section');

      [categoriesSection, mainSection, sidebar].filter(Boolean).forEach((node) => {
        observer.observe(node);
      });

      sidebar?.querySelectorAll('[data-sidebar-part]').forEach((node) => {
        observer.observe(node);
      });
    }

    window.addEventListener('resize', measure);

    const fontsReady = document.fonts?.ready;
    if (fontsReady) {
      fontsReady.then(measure).catch(() => {});
    }

    const delayed = window.setTimeout(measure, 100);
    const delayedLong = window.setTimeout(measure, 500);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.clearTimeout(delayed);
      window.clearTimeout(delayedLong);
    };
  }, [enabled, isCategoryPage, isHomePage, isProductPage, pathname]);

  return catalogMaxHeight;
}
